// src/pages/userLogin.jsx

import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";

import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";

import {
  Visibility,
  VisibilityOff,
  Phone,
  Lock,
  Business,
} from "@mui/icons-material";

import { tokens } from "../theme";

const Login = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/companies/login`,
        {
          emailOrPhone,
          password,
        }
      );

      const data = res.data;

      if (!data.success) {
        throw new Error(data.message || "Login failed");
      }

      // Save token
      localStorage.setItem(
        "token",
        data.data.accessToken
      );

      // Save company info
      localStorage.setItem(
        "company",
        JSON.stringify(data.data.company)
      );

      localStorage.setItem("authType", "company");

      // Redirect to dashboard
      navigate("/");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`
            : `linear-gradient(135deg, #f5f7fa 0%, #dbe7ff 100%)`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 1000,
          overflow: "hidden",
          borderRadius: "20px",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
          },
        }}
      >
        {/* LEFT SIDE */}
        <Box
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(180deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[700]} 100%)`
                : `linear-gradient(180deg, ${colors.blueAccent[400]} 0%, ${colors.blueAccent[600]} 100%)`,
            color: "#fff",
            p: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: {
              xs: "center",
              md: "left",
            },
          }}
        >
          <Business
            sx={{
              fontSize: 70,
              mb: 2,
              mx: { xs: "auto", md: 0 },
            }}
          />

          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "28px",
                sm: "36px",
                md: "42px",
              },
            }}
          >
            Distributor ERP
          </Typography>

          <Typography
            sx={{
              mt: 2,
              opacity: 0.9,
              fontSize: {
                xs: "14px",
                sm: "16px",
              },
            }}
          >
            Manage sales, inventory, warehouses,
            reports, and company operations
            in one powerful platform.
          </Typography>

          <Box
            sx={{
              mt: 5,
              display: {
                xs: "none",
                md: "block",
              },
            }}
          >
            <Typography variant="h6" mb={2}>
              Platform Features
            </Typography>

            <Typography mb={1}>
              ✔ Sales & Distribution
            </Typography>

            <Typography mb={1}>
              ✔ Purchase & Inventory
            </Typography>

            <Typography mb={1}>
              ✔ Warehouse Management
            </Typography>

            <Typography mb={1}>
              ✔ Reports & Analytics
            </Typography>

            <Typography mb={1}>
              ✔ Multi-user Access
            </Typography>
          </Box>
        </Box>

        {/* RIGHT SIDE */}
        <Box
          sx={{
            p: {
              xs: 3,
              sm: 4,
              md: 6,
            },
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#fff",
          }}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            mb={1}
            color={colors.grey[100]}
            sx={{
              fontSize: {
                xs: "28px",
                sm: "34px",
              },
            }}
          >
            Welcome Back
          </Typography>

          <Typography
            color={colors.grey[300]}
            mb={4}
          >
            Sign in to your company account
          </Typography>

          {error && (
            <Box
              sx={{
                backgroundColor:
                  colors.redAccent[500],
                color: "#fff",
                p: 2,
                borderRadius: 2,
                mb: 3,
              }}
            >
              {error}
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <TextField
              fullWidth
              variant="filled"
              label="Email or Phone"
              value={emailOrPhone}
              onChange={(e) =>
                setEmailOrPhone(e.target.value)
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              variant="filled"
              type={
                showPassword ? "text" : "password"
              }
              label="Password"
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPassword(
                          !showPassword
                        )
                      }
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: "bold",
                backgroundColor:
                  colors.greenAccent[500],
                color: "#fff",

                "&:hover": {
                  backgroundColor:
                    colors.greenAccent[600],
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{ color: "#fff" }}
                />
              ) : (
                "Login"
              )}
            </Button>
          </Box>

          <Typography
            mt={4}
            textAlign="center"
            color={colors.grey[300]}
          >
            Don’t have a company account?{" "}
            <Link
              to="/register-company"
              style={{
                color: colors.blueAccent[400],
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              Register Company
            </Link>
          </Typography>

          <Typography
            variant="body2"
            textAlign="center"
            mt={5}
            color={colors.grey[400]}
          >
            © {new Date().getFullYear()} Distributor ERP
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;